import React, { useMemo, useRef, useEffect } from "react";
import * as d3 from "d3";

function rollingAvg(data, valueKey, window, outputKey) {
  return data.map((d, i) => {
    const start = Math.max(0, i - window + 1);
    const slice = data.slice(start, i + 1).map(x => x[valueKey]).filter(v => v != null);
    return { ...d, [outputKey]: slice.length ? slice.reduce((s, v) => s + v, 0) / slice.length : null };
  });
}

function toColloquial(temp) {
  const t = Math.round(parseFloat(temp));
  const decade = Math.floor(t / 10) * 10;
  const rem = ((t % 10) + 10) % 10;
  if (rem <= 2) return `low ${decade}s`;
  if (rem <= 6) return `mid-${decade}s`;
  return `upper ${decade}s`;
}

export function SeasonalShiftChart({ dailyMetricsData, unit, shiftOverride }) {
  const containerRef = useRef(null);
  const svgRef = useRef(null);

  const { seasonalData, currentYearSmoothed, shift } = useMemo(() => {
    if (!dailyMetricsData) return { seasonalData: [], currentYearSmoothed: [], shift: null };

    const today = new Date();
    today.setUTCHours(0, 0, 0, 0);
    const currentYear = today.getUTCFullYear();
    const toU = (c) => unit === "F" ? (c * 9) / 5 + 32 : c;

    const entries = Object.entries(dailyMetricsData)
      .map(([ds, v]) => ({ date: new Date(ds + "T00:00:00Z"), ...v }))
      .sort((a, b) => a.date - b.date);

    // 1951-80 seasonal curve projected onto current year
    const mmddSeen = new Set();
    const seasonal = entries
      .filter(e => {
        const mmdd = e.date.toISOString().slice(5, 10);
        if (mmddSeen.has(mmdd) || e.baseline_avg == null) return false;
        mmddSeen.add(mmdd);
        return true;
      })
      .map(e => ({
        date: new Date(`${currentYear}-${e.date.toISOString().slice(5, 10)}T00:00:00Z`),
        baseline_avg: toU(e.baseline_avg),
      }))
      .sort((a, b) => a.date - b.date);

    // 30-day trailing rolling avg of current year actuals, stop at today
    const cyEntries = entries
      .filter(e => e.date.getUTCFullYear() === currentYear && e.actual != null && e.date <= today);
    const smoothed = rollingAvg(cyEntries, "actual", 30, "smoothed_actual")
      .map(e => ({ ...e, smoothed_actual: e.smoothed_actual != null ? toU(e.smoothed_actual) : null }));

    // Compute crossover: last value of smoothed line vs 1951-80 curve
    const lastSmoothed = smoothed.filter(e => e.smoothed_actual != null).slice(-1)[0];
    let computedShift = null;
    if (lastSmoothed) {
      const refTemp = unit === "F"
        ? (lastSmoothed.smoothed_actual - 32) * 5 / 9  // back to C for crossover
        : lastSmoothed.smoothed_actual;

      const mmddMap = new Map();
      for (const e of entries) {
        if (e.baseline_avg == null) continue;
        const mmdd = e.date.toISOString().slice(5, 10);
        if (!mmddMap.has(mmdd)) mmddMap.set(mmdd, e.baseline_avg);
      }
      const curve = Array.from(mmddMap.entries())
        .map(([mmdd, avg]) => ({ mmdd, avg }))
        .sort((a, b) => a.mmdd.localeCompare(b.mmdd));

      let crossoverMMDD = null;
      for (let i = 1; i < curve.length; i++) {
        if (curve[i - 1].avg < refTemp && curve[i].avg >= refTemp) {
          crossoverMMDD = curve[i].mmdd;
          break;
        }
      }
      const crossoverDate = crossoverMMDD
        ? new Date(`${currentYear}-${crossoverMMDD}T00:00:00Z`)
        : null;
      const shiftDays = crossoverDate
        ? Math.round((crossoverDate - today) / (1000 * 60 * 60 * 24))
        : null;

      computedShift = {
        avg30: lastSmoothed.smoothed_actual,
        crossoverDate,
        shiftDays: shiftDays != null ? Math.abs(shiftDays) : null,
        isEarlier: shiftDays != null && shiftDays > 0,
        crossoverMonthStr: crossoverDate
          ? crossoverDate.toLocaleDateString("en-US", { month: "long", day: "numeric", timeZone: "UTC" })
          : null,
      };
    }

    return { seasonalData: seasonal, currentYearSmoothed: smoothed, shift: computedShift };
  }, [dailyMetricsData, unit]);

  useEffect(() => {
    if (!svgRef.current || !containerRef.current || !seasonalData.length) return;

    const today = new Date();
    today.setUTCHours(0, 0, 0, 0);
    const currentYear = today.getUTCFullYear();

    const totalWidth = containerRef.current.getBoundingClientRect().width || 800;
    const margin = { top: 16, right: 16, bottom: 28, left: 50 };
    const width = totalWidth - margin.left - margin.right;
    const height = 320;

    // Use the same shift as the card's text (the pipeline's, when provided).
    const activeShift = shiftOverride ?? shift;
    const crossoverDate = activeShift?.crossoverDate ?? null;
    const focusStart = new Date(Math.min(today, crossoverDate ?? today));
    focusStart.setUTCDate(focusStart.getUTCDate() - 60);
    const focusEnd = new Date(Math.max(today, crossoverDate ?? today));
    focusEnd.setUTCDate(focusEnd.getUTCDate() + 60);
    const yearStart = new Date(`${currentYear}-01-01T00:00:00Z`);
    const yearEnd = new Date(`${currentYear}-12-31T00:00:00Z`);
    const domainStart = focusStart < yearStart ? yearStart : focusStart;
    const domainEnd = focusEnd > yearEnd ? yearEnd : focusEnd;

    const allVals = [
      ...seasonalData.filter(d => d.date >= domainStart && d.date <= domainEnd).map(d => d.baseline_avg),
      ...currentYearSmoothed.filter(d => d.smoothed_actual != null && d.date >= domainStart).map(d => d.smoothed_actual),
      shift?.avg30,
    ].filter(v => v != null);

    const [vMin, vMax] = d3.extent(allVals);
    const pad = Math.max((vMax - vMin) * 0.15, 0.5);

    const xScale = d3.scaleUtc().domain([domainStart, domainEnd]).range([0, width]);
    const yScale = d3.scaleLinear().domain([vMin - pad, vMax + pad]).range([height, 0]).nice();

    const svg = d3.select(svgRef.current);
    svg.selectAll("*").remove();
    svg.attr("width", totalWidth).attr("height", height + margin.top + margin.bottom);

    const g = svg.append("g").attr("transform", `translate(${margin.left},${margin.top})`);

    function styleAxis(sel) {
      sel.select(".domain").remove();
      sel.selectAll(".tick line").attr("stroke", "#3a3530");
      sel.selectAll(".tick text").attr("fill", "#a89880").style("font-size", "10px")
        .style("font-family", "Helvetica Neue, Arial, sans-serif");
    }

    g.selectAll(".grid-line")
      .data(yScale.ticks(4))
      .enter().append("line")
      .attr("x1", 0).attr("x2", width)
      .attr("y1", d => yScale(d)).attr("y2", d => yScale(d))
      .attr("stroke", "#2e2b27").attr("stroke-width", 1);

    // 1951-80 baseline (dashed)
    g.append("path")
      .datum(seasonalData)
      .attr("fill", "none")
      .attr("stroke", "#8b7355")
      .attr("stroke-width", 1.5)
      .attr("stroke-dasharray", "4,3")
      .style("opacity", 0.6)
      .attr("d", d3.line()
        .x(d => xScale(d.date))
        .y(d => yScale(d.baseline_avg))
        .defined(d => d.baseline_avg != null && d.date >= domainStart && d.date <= domainEnd)
        .curve(d3.curveMonotoneX));

    // 30-day rolling avg of current year (orange)
    g.append("path")
      .datum(currentYearSmoothed)
      .attr("fill", "none")
      .attr("stroke", "#ff6b47")
      .attr("stroke-width", 2.25)
      .style("opacity", 0.85)
      .attr("d", d3.line()
        .x(d => xScale(d.date))
        .y(d => yScale(d.smoothed_actual))
        .defined(d => d.smoothed_actual != null && d.date >= domainStart && d.date <= domainEnd)
        .curve(d3.curveMonotoneX));

    // Today vertical line
    const todayX = xScale(today);
    g.append("line")
      .attr("x1", todayX).attr("x2", todayX)
      .attr("y1", 0).attr("y2", height)
      .attr("stroke", "#f2ede6").attr("stroke-width", 1)
      .attr("stroke-dasharray", "4,3").style("opacity", 0.35);

    // Horizontal reference at 30-day avg
    if (shift?.avg30 != null) {
      const yRef = yScale(shift.avg30);
      g.append("line")
        .attr("x1", 0).attr("x2", width)
        .attr("y1", yRef).attr("y2", yRef)
        .attr("stroke", "#f2ede6").attr("stroke-width", 1)
        .attr("stroke-dasharray", "5,4").style("opacity", 0.3);
    }

    // Crossover annotation
    // Earlier (spring): the old normal reaches these temperatures ahead of today.
    // Later (autumn): it last had them behind today. Shade the gap either way.
    if (crossoverDate && (activeShift?.isEarlier || activeShift?.isLater)) {
      const crossoverX = xScale(crossoverDate);

      g.append("rect")
        .attr("x", Math.min(todayX, crossoverX)).attr("width", Math.abs(crossoverX - todayX))
        .attr("y", 0).attr("height", height)
        .attr("fill", "#ff4d1c").style("opacity", 0.07);

      g.append("line")
        .attr("x1", crossoverX).attr("x2", crossoverX)
        .attr("y1", 0).attr("y2", height)
        .attr("stroke", "#8b7355").attr("stroke-width", 1)
        .attr("stroke-dasharray", "4,3").style("opacity", 0.6);

      const anchor = crossoverX > width * 0.72 ? "end" : "start";
      const lx = crossoverX > width * 0.72 ? crossoverX - 4 : crossoverX + 4;
      g.append("text")
        .attr("x", lx).attr("y", height - 6)
        .attr("text-anchor", anchor).attr("fill", "#8b7355")
        .style("font-size", "9px").style("font-family", "Helvetica Neue, Arial, sans-serif")
        .text(crossoverDate.toLocaleDateString("en-US", { month: "short", day: "numeric", timeZone: "UTC" }));

      const midX = (todayX + crossoverX) / 2;
      g.append("text")
        .attr("x", midX).attr("y", 14)
        .attr("text-anchor", "middle").attr("fill", "#ff6b47")
        .style("font-size", "10px").style("font-weight", "700")
        .style("font-family", "Helvetica Neue, Arial, sans-serif")
        .text(`${activeShift.shiftDays} days ${activeShift.isEarlier ? "earlier" : "later"}`);
    }

    g.append("g")
      .attr("transform", `translate(0,${height})`)
      .call(d3.axisBottom(xScale).ticks(d3.utcMonth.every(1)).tickFormat(d3.utcFormat("%b")).tickSizeOuter(0))
      .call(styleAxis);

    g.append("g")
      .call(d3.axisLeft(yScale).ticks(4).tickFormat(d => `${d.toFixed(0)}°`))
      .call(styleAxis);

  }, [seasonalData, currentYearSmoothed, shift, unit,
      shiftOverride?.isEarlier, shiftOverride?.isLater, shiftOverride?.shiftDays, shiftOverride?.crossoverMonthStr]);

  if (!seasonalData.length) return null;

  return (
    <section className="recent-anomaly-section" ref={containerRef}>
      <div className="ra-card">
        <div className="ra-card-eyebrow">Seasonal shift</div>
        {(shiftOverride ?? shift)?.exceedsBaseline ? (
          <div className="ra-card-sub">
            The past 30 days averaged{" "}
            <span className="temp-hot">{(shiftOverride ?? shift)?.avg30?.toFixed != null ? parseFloat((shiftOverride ?? shift).avg30).toFixed(1) : "—"}°{unit}</span>.{" "}
            {(() => {
              const s = shiftOverride ?? shift;
              const bigDiff = s?.baselineTodayAvg != null && s?.avg30 != null &&
                parseFloat(s.avg30) - parseFloat(s.baselineTodayAvg) > 4;
              return bigDiff ? (
                <>
                  A day like this used to average in the{" "}
                  <span className="temp-cool">{toColloquial(s.baselineTodayAvg)}</span>{" "}
                  on this date — today's temperatures are beyond anything in the 1951–80 baseline record.
                </>
              ) : (
                <>That's warmer than temperatures at any point in the 1951–80 baseline record.</>
              );
            })()}
          </div>
        ) : (shiftOverride ?? shift)?.isEarlier ? (
          <>
            <div className="ra-card-count temp-hot">
              {(shiftOverride ?? shift).shiftDays} days
            </div>
            <div className="ra-card-sub">
              ahead of the 1951–80 calendar: warm weather is arriving early. The past 30 days averaged{" "}
              <span className="temp-hot">{parseFloat((shiftOverride ?? shift).avg30).toFixed(1)}°{unit}</span>,
              typical of{" "}
              <span className="temp-hot">{(shiftOverride ?? shift).crossoverMonthStr}</span> back then.{" "}
              {(() => {
                const s = shiftOverride ?? shift;
                const bigDiff = s?.baselineTodayAvg != null && s?.avg30 != null &&
                  parseFloat(s.avg30) - parseFloat(s.baselineTodayAvg) > 4;
                return bigDiff ? (
                  <>
                    This time of year used to average in the{" "}
                    <span className="temp-cool">{toColloquial(s.baselineTodayAvg)}</span>.
                  </>
                ) : null;
              })()}
            </div>
          </>
        ) : (shiftOverride ?? shift)?.isLater ? (
          <>
            <div className="ra-card-count temp-hot">
              {(shiftOverride ?? shift).shiftDays} days
            </div>
            <div className="ra-card-sub">
              behind the 1951–80 calendar: summer warmth is lingering. The past 30 days averaged{" "}
              <span className="temp-hot">{parseFloat((shiftOverride ?? shift).avg30).toFixed(1)}°{unit}</span>,
              typical of{" "}
              <span className="temp-hot">{(shiftOverride ?? shift).crossoverMonthStr}</span> back then.{" "}
              {(() => {
                const s = shiftOverride ?? shift;
                const bigDiff = s?.baselineTodayAvg != null && s?.avg30 != null &&
                  parseFloat(s.avg30) - parseFloat(s.baselineTodayAvg) > 4;
                return bigDiff ? (
                  <>
                    This time of year used to average in the{" "}
                    <span className="temp-cool">{toColloquial(s.baselineTodayAvg)}</span>.
                  </>
                ) : null;
              })()}
            </div>
          </>
        ) : (
          <div className="ra-card-sub">
            The past 30 days have been in line with — or cooler than — the 1951–80 historical average for this time of year.{" "}
            However, temperatures over the past two decades have been running consistently warmer than those historical norms.{" "}
            <a
              href="#scrolly-new-normal"
              className="ra-card-link"
              onClick={e => {
                e.preventDefault();
                const el = document.getElementById("scrolly-new-normal");
              if (el) {
                // On mobile the step is min-height:150vh with the card pinned to
                // the bottom (align-items: flex-end). Scroll to the card itself,
                // not the step wrapper, so it lands in the centre of the viewport.
                const card = el.querySelector(".step-card") || el;
                const cardRect = card.getBoundingClientRect();
                const targetY = window.scrollY + cardRect.top - window.innerHeight * 0.55;
                window.scrollTo({ top: targetY, behavior: "smooth" });
              }
              }}
            >
              See the new normal ↓
            </a>
          </div>
        )}
      </div>
      <svg ref={svgRef} style={{ width: "100%", display: "block" }} />
    </section>
  );
}
